import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import { bookingAPI, paymentAPI } from '../../services/api';
import { CURRENCIES, VALIDATION_RULES } from '../../utils/constants';
import ServiceSelector from '../service/ServiceSelector';

const UserBookingForm = ({ onClose, onSuccess, isDarkMode = false }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [flightLocations, setFlightLocations] = useState([]);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    currency: 'GBP', // Default to GBP for UK services
    description: '',
    flightDetails: {
      from: '',
      to: '',
      flightNumber: '',
      departureDate: '',
      arrivalDate: ''
    }
  });
  const [loading, setLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [errors, setErrors] = useState({});

  // Get current date-time in local timezone for min attribute
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  
  // Get minimum arrival date based on departure date
  const getMinArrivalDate = () => {
    if (formData.flightDetails.departureDate) {
      return formData.flightDetails.departureDate;
    }
    return getCurrentDateTime();
  };

  useEffect(() => {
    loadFlightLocations();
  }, []);

  const loadFlightLocations = async () => {
    try {
      const response = await paymentAPI.getFlightLocations();
      if (response.success) {
        setFlightLocations(response.data);
      }
    } catch (error) {
      console.error('Failed to load flight locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Service selection validation
    if (!selectedService) {
      newErrors.service = 'Please select a service type and level';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < VALIDATION_RULES.DESCRIPTION.MIN_LENGTH) {
      newErrors.description = `Description must be at least ${VALIDATION_RULES.DESCRIPTION.MIN_LENGTH} characters`;
    } else if (formData.description.trim().length > VALIDATION_RULES.DESCRIPTION.MAX_LENGTH) {
      newErrors.description = `Description must be no more than ${VALIDATION_RULES.DESCRIPTION.MAX_LENGTH} characters`;
    }

    // Flight details validation
    if (!formData.flightDetails.from) {
      newErrors['flightDetails.from'] = 'Origin airport is required';
    }

    if (!formData.flightDetails.to) {
      newErrors['flightDetails.to'] = 'Destination airport is required';
    }

    if (!formData.flightDetails.flightNumber.trim()) {
      newErrors['flightDetails.flightNumber'] = 'Flight number is required';
    } else if (formData.flightDetails.flightNumber.trim().length < 2) {
      newErrors['flightDetails.flightNumber'] = 'Flight number must be at least 2 characters';
    }

    if (!formData.flightDetails.departureDate) {
      newErrors['flightDetails.departureDate'] = 'Departure date is required';
    } else {
      const departureDate = new Date(formData.flightDetails.departureDate);
      const now = new Date();
      if (departureDate < now) {
        newErrors['flightDetails.departureDate'] = 'Departure date cannot be in the past';
      }
    }

    if (!formData.flightDetails.arrivalDate) {
      newErrors['flightDetails.arrivalDate'] = 'Arrival date is required';
    } else {
      const arrivalDate = new Date(formData.flightDetails.arrivalDate);
      const departureDate = formData.flightDetails.departureDate ? new Date(formData.flightDetails.departureDate) : null;
      
      if (departureDate && arrivalDate < departureDate) {
        newErrors['flightDetails.arrivalDate'] = 'Arrival date must be after departure date';
      }
    }

    // Currency validation
    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    } else if (!CURRENCIES.find(c => c.value === formData.currency)) {
      newErrors.currency = 'Please select a valid currency';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested flight details
    if (name.startsWith('flightDetails.')) {
      const flightField = name.split('.')[1];
      
      // Special handling for departure date changes
      if (flightField === 'departureDate') {
        setFormData(prev => {
          const newFlightDetails = {
            ...prev.flightDetails,
            departureDate: value
          };
          
          // If arrival date is before new departure date, clear it
          if (prev.flightDetails.arrivalDate && value && new Date(prev.flightDetails.arrivalDate) < new Date(value)) {
            newFlightDetails.arrivalDate = '';
            // Clear arrival date error if exists
            setErrors(prevErrors => ({
              ...prevErrors,
              'flightDetails.arrivalDate': ''
            }));
          }
          
          return {
            ...prev,
            flightDetails: newFlightDetails
          };
        });
      } else {
        setFormData(prev => ({
          ...prev,
          flightDetails: {
            ...prev.flightDetails,
            [flightField]: value
          }
        }));
      }
      
      // Clear nested field error when user starts typing
      const errorKey = `flightDetails.${flightField}`;
      if (errors[errorKey]) {
        setErrors(prev => ({
          ...prev,
          [errorKey]: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  const handleServiceSelect = useCallback((service) => {
    setSelectedService(service);
    
    // Clear service error when service is selected
    if (errors.service) {
      setErrors(prev => ({
        ...prev,
        service: ''
      }));
    }

    // Auto-generate description based on selected service
    if (service && !formData.description) {
      const description = `${service.category.name} ${service.level.name} Service - ${service.level.description}`;
      setFormData(prev => ({
        ...prev,
        description: description
      }));
    }
  }, [errors.service, formData.description]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    setLoading(true);

    try {
      // Prepare service details exactly like the admin form
      const serviceDetails = {
        categoryId: selectedService.category.id,
        categoryName: selectedService.category.name,
        levelId: selectedService.level.id,
        levelName: selectedService.level.name,
        basePrice: selectedService.pricing.basePrice,
        additionalServices: {
          bags: {
            count: selectedService.additionalServices?.bags || 1,
            range: selectedService.level.additionalServices?.bags ? 
              Object.keys(selectedService.level.additionalServices.bags).find(range => {
                const bagCount = selectedService.additionalServices?.bags || 1;
                const [min, max] = range.split('-').map(Number);
                return bagCount >= min && bagCount <= max;
              }) : '1-4',
            fee: selectedService.pricing.additionalFees || 0
          }
        },
        features: selectedService.level.features || [],
        pricingSource: selectedService.pricing.pricingSource || 'static'
      };

      // Prepare price breakdown
      const priceBreakdown = {
        basePrice: selectedService.pricing.basePrice,
        additionalFees: selectedService.pricing.additionalFees || 0,
        totalPrice: selectedService.pricing.totalPrice,
        currency: selectedService.pricing.currency
      };

      const response = await bookingAPI.createBookingRequest({
        phoneNumber: formData.phoneNumber.trim() || undefined,
        amount: selectedService.pricing.totalPrice,
        currency: selectedService.pricing.currency,
        serviceType: selectedService.category.name.toUpperCase(),
        description: formData.description.trim(),
        serviceDetails: serviceDetails,
        priceBreakdown: priceBreakdown,
        flightDetails: {
          from: formData.flightDetails.from,
          to: formData.flightDetails.to,
          flightNumber: formData.flightDetails.flightNumber,
          departureDate: formData.flightDetails.departureDate,
          arrivalDate: formData.flightDetails.arrivalDate
        }
      });

      if (response.success) {
        toast.success('Booking request created successfully! Check your email for confirmation.');
        onSuccess && onSuccess(response.data);
        onClose();
      } else {
        toast.error(response.message || 'Failed to create booking request');
      }
    } catch (error) {
      console.error('Error creating booking request:', error);
      toast.error('Failed to create booking request');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className={`rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark' : 'bg-white'
      }`}>
        <div className={`flex items-center justify-between p-6 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>New Booking Request</h2>
          <button
            onClick={onClose}
            className={`transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Service Selection */}
          <div>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Service Selection</h3>
            <ServiceSelector 
              onServiceSelect={handleServiceSelect}
              selectedService={selectedService}
              isDarkMode={isDarkMode}
            />
            {errors.service && (
              <p className={`text-sm mt-2 transition-colors duration-300 ${
                isDarkMode ? 'text-red-400' : 'text-red-600'
              }`}>{errors.service}</p>
            )}
          </div>

          {/* Contact Information */}
          <div>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Contact Information</h3>
            <div className="form-group">
              <label htmlFor="phoneNumber" className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`input ${
                  isDarkMode ? 'bg-skyone-inset-dark text-white border-skyone-border-dark' : 'bg-white text-gray-900 border-gray-300'
                }`}
                placeholder="Enter your phone number (optional)"
                disabled={loading}
              />
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                We'll use this to contact you about your booking
              </p>
            </div>
          </div>

          {/* Flight Details */}
          <div>
            <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Flight Details</h3>
            <p className={`text-sm mb-4 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Select the airports for this flight service
            </p>
            
            {/* From and To Locations */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="form-group">
                <label htmlFor="flightDetails.from" className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  From *
                </label>
                <select
                  id="flightDetails.from"
                  name="flightDetails.from"
                  value={formData.flightDetails.from}
                  onChange={handleInputChange}
                  className={`input ${errors['flightDetails.from'] ? 'input-error' : ''} ${
                    isDarkMode ? 'bg-skyone-inset-dark text-white border-skyone-border-dark' : 'bg-white text-gray-900 border-gray-300'
                  }`}
                  disabled={loading || loadingLocations}
                >
                  <option value="">Select origin airport</option>
                  {flightLocations.map((location) => (
                    <option key={location.value} value={location.value}>
                      {location.label}
                    </option>
                  ))}
                </select>
                {errors['flightDetails.from'] && (
                  <p className={`text-sm mt-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-red-400' : 'text-red-600'
                  }`}>{errors['flightDetails.from']}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="flightDetails.to" className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  To *
                </label>
                <select
                  id="flightDetails.to"
                  name="flightDetails.to"
                  value={formData.flightDetails.to}
                  onChange={handleInputChange}
                  className={`input ${errors['flightDetails.to'] ? 'input-error' : ''} ${
                    isDarkMode ? 'bg-skyone-inset-dark text-white border-skyone-border-dark' : 'bg-white text-gray-900 border-gray-300'
                  }`}
                  disabled={loading || loadingLocations}
                >
                  <option value="">Select destination airport</option>
                  {flightLocations.map((location) => (
                    <option key={location.value} value={location.value}>
                      {location.label}
                    </option>
                  ))}
                </select>
                {errors['flightDetails.to'] && (
                  <p className={`text-sm mt-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-red-400' : 'text-red-600'
                  }`}>{errors['flightDetails.to']}</p>
                )}
              </div>
            </div>

            {/* Flight Number and Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="form-group">
                <label htmlFor="flightDetails.flightNumber" className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Flight Number *
                </label>
                <input
                  type="text"
                  id="flightDetails.flightNumber"
                  name="flightDetails.flightNumber"
                  value={formData.flightDetails.flightNumber}
                  onChange={handleInputChange}
                  className={`input ${errors['flightDetails.flightNumber'] ? 'input-error' : ''} ${
                    isDarkMode ? 'bg-skyone-inset-dark text-white border-skyone-border-dark' : 'bg-white text-gray-900 border-gray-300'
                  }`}
                  placeholder="e.g., BA123"
                  disabled={loading}
                />
                {errors['flightDetails.flightNumber'] && (
                  <p className={`text-sm mt-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-red-400' : 'text-red-600'
                  }`}>{errors['flightDetails.flightNumber']}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="flightDetails.departureDate" className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Departure Date *
                </label>
                <input
                  type="datetime-local"
                  id="flightDetails.departureDate"
                  name="flightDetails.departureDate"
                  value={formData.flightDetails.departureDate}
                  onChange={handleInputChange}
                  min={getCurrentDateTime()}
                  className={`input ${errors['flightDetails.departureDate'] ? 'input-error' : ''} ${
                    isDarkMode ? 'bg-skyone-inset-dark text-white border-skyone-border-dark' : 'bg-white text-gray-900 border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors['flightDetails.departureDate'] && (
                  <p className={`text-sm mt-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-red-400' : 'text-red-600'
                  }`}>{errors['flightDetails.departureDate']}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="flightDetails.arrivalDate" className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Arrival Date *
                </label>
                <input
                  type="datetime-local"
                  id="flightDetails.arrivalDate"
                  name="flightDetails.arrivalDate"
                  value={formData.flightDetails.arrivalDate}
                  onChange={handleInputChange}
                  min={getMinArrivalDate()}
                  className={`input ${errors['flightDetails.arrivalDate'] ? 'input-error' : ''} ${
                    isDarkMode ? 'bg-skyone-inset-dark text-white border-skyone-border-dark' : 'bg-white text-gray-900 border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors['flightDetails.arrivalDate'] && (
                  <p className={`text-sm mt-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-red-400' : 'text-red-600'
                  }`}>{errors['flightDetails.arrivalDate']}</p>
                )}
                {formData.flightDetails.departureDate && !formData.flightDetails.arrivalDate && (
                  <p className={`text-sm mt-1 transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                    Arrival must be after {new Date(formData.flightDetails.departureDate).toLocaleString('en-GB', { 
                      dateStyle: 'medium', 
                      timeStyle: 'short' 
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Payment Details */}
          {selectedService && (
            <div>
              <h3 className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>Payment Details</h3>
              <div className={`border rounded-lg p-4 transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-inset-dark border-skyone-border-dark' : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Service Selected
                    </label>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {selectedService.category.name} - {selectedService.level.name}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Total Amount
                    </label>
                    <p className={`text-lg font-bold transition-colors duration-300 ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      {formatPrice(selectedService.pricing.totalPrice, selectedService.pricing.currency)}
                    </p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Base Price
                    </label>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {formatPrice(selectedService.pricing.basePrice, selectedService.pricing.currency)}
                    </p>
                  </div>
                  {selectedService.pricing.additionalFees > 0 && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Additional Fees
                      </label>
                      <p className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {formatPrice(selectedService.pricing.additionalFees, selectedService.pricing.currency)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Currency Selection */}
          <div className="form-group">
            <label htmlFor="currency" className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Currency *
            </label>
            <select
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleInputChange}
              className={`input ${errors.currency ? 'input-error' : ''} ${
                isDarkMode ? 'bg-skyone-inset-dark text-white border-skyone-border-dark' : 'bg-white text-gray-900 border-gray-300'
              }`}
              disabled={loading}
            >
              {CURRENCIES.map((currency) => (
                <option key={currency.value} value={currency.value}>
                  {currency.label}
                </option>
              ))}
            </select>
            {errors.currency && (
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? 'text-red-400' : 'text-red-600'
              }`}>{errors.currency}</p>
            )}
            <p className={`text-sm mt-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Note: Service prices are calculated in GBP. Currency conversion will be handled by Stripe.
            </p>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description" className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className={`input ${errors.description ? 'input-error' : ''} ${
                isDarkMode ? 'bg-skyone-inset-dark text-white border-skyone-border-dark' : 'bg-white text-gray-900 border-gray-300'
              }`}
              placeholder="Describe what this payment is for..."
              rows={4}
              disabled={loading}
            />
            {errors.description && (
              <p className={`text-sm mt-1 transition-colors duration-300 ${
                isDarkMode ? 'text-red-400' : 'text-red-600'
              }`}>{errors.description}</p>
            )}
            <p className={`text-sm mt-1 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {formData.description.length}/{VALIDATION_RULES.DESCRIPTION.MAX_LENGTH} characters
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className={`relative w-24 h-10 text-sm font-medium rounded-md border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-primary-700 text-white hover:bg-primary-600 border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500' 
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300 border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500'
              }`}
            >
              <span className="absolute inset-0 flex items-center justify-center">
                Cancel
              </span>
            </button>
            <button
              type="submit"
              disabled={loading || !selectedService}
              className={`relative w-48 h-10 text-sm font-medium rounded-md border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-blue-600 text-white hover:bg-blue-700 border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500'
              }`}
            >
              <span className="absolute inset-0 flex items-center justify-center">
                {loading ? (
                  <>
                    <div className={`w-4 h-4 border-2 rounded-full animate-spin mr-2 ${
                      isDarkMode ? 'border-gray-300 border-t-white' : 'border-gray-200 border-t-white'
                    }`}></div>
                    Creating...
                  </>
                ) : (
                  'Create Booking Request'
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserBookingForm;